/**
 * Web Bluetooth thermal printer (ESC/POS) integration.
 *
 * Most generic BLE thermal printers expose a writable characteristic on one of
 * a handful of well-known service UUIDs. We probe the common ones and write
 * raw ESC/POS bytes in chunks (typical MTU ~20 bytes for older firmware,
 * ~512 for modern; we use 100 for safety).
 *
 * Web Bluetooth: Chrome/Edge desktop + Android. Not supported on iOS Safari
 * or Firefox — caller should hide the UI via isWebBluetoothSupported().
 */

import type { OrderDTO } from '@/types/api';
import { groupOrderItems, itemQty } from '@/lib/orderItems';

// Generic printer service UUIDs we'll try, in priority order.
const PRINTER_SERVICES: BluetoothServiceUUID[] = [
  '000018f0-0000-1000-8000-00805f9b34fb', // most common BLE thermal-printer service
  '0000ff00-0000-1000-8000-00805f9b34fb',
  '0000ff12-0000-1000-8000-00805f9b34fb',
  'e7810a71-73ae-499d-8c15-faa9aef0c3f2',
];

interface BluetoothDeviceLike {
  name?: string;
  gatt?: {
    connected: boolean;
    connect: () => Promise<BluetoothRemoteGATTServerLike>;
    disconnect: () => void;
  };
  addEventListener?: (type: 'gattserverdisconnected', cb: () => void) => void;
}

interface BluetoothRemoteGATTServerLike {
  getPrimaryServices: () => Promise<BluetoothRemoteGATTServiceLike[]>;
  getPrimaryService: (uuid: BluetoothServiceUUID) => Promise<BluetoothRemoteGATTServiceLike>;
}

interface BluetoothRemoteGATTServiceLike {
  getCharacteristics: () => Promise<BluetoothRemoteGATTCharacteristicLike[]>;
}

interface BluetoothRemoteGATTCharacteristicLike {
  uuid: string;
  properties: {
    write: boolean;
    writeWithoutResponse: boolean;
  };
  writeValueWithoutResponse?: (value: BufferSource) => Promise<void>;
  writeValue?: (value: BufferSource) => Promise<void>;
}

interface BluetoothApi {
  requestDevice: (options: {
    filters?: { services?: BluetoothServiceUUID[]; namePrefix?: string }[];
    optionalServices?: BluetoothServiceUUID[];
    acceptAllDevices?: boolean;
  }) => Promise<BluetoothDeviceLike>;
}

type BluetoothServiceUUID = string;

let device: BluetoothDeviceLike | null = null;
let writeChar: BluetoothRemoteGATTCharacteristicLike | null = null;

export function isWebBluetoothSupported(): boolean {
  return typeof navigator !== 'undefined' && 'bluetooth' in navigator;
}

export function isPrinterConnected(): boolean {
  return Boolean(device?.gatt?.connected && writeChar);
}

export function connectedPrinterName(): string | null {
  return device?.name ?? null;
}

/**
 * Prompts the user with the OS bluetooth chooser and connects to the picked printer.
 * Requires a user gesture (button click) — can't be called silently.
 */
export async function pairPrinter(): Promise<string> {
  if (!isWebBluetoothSupported()) throw new Error('Web Bluetooth not supported');
  const nav = navigator as Navigator & { bluetooth: BluetoothApi };

  const requested = await nav.bluetooth.requestDevice({
    filters: PRINTER_SERVICES.map((s) => ({ services: [s] })),
    optionalServices: PRINTER_SERVICES,
  });

  if (!requested.gatt) throw new Error('No GATT on device');
  const server = await requested.gatt.connect();
  const services = await server.getPrimaryServices();

  // Find first writable characteristic across discovered services.
  let chosen: BluetoothRemoteGATTCharacteristicLike | null = null;
  for (const svc of services) {
    const chars = await svc.getCharacteristics();
    chosen = chars.find((c) => c.properties.write || c.properties.writeWithoutResponse) ?? null;
    if (chosen) break;
  }
  if (!chosen) {
    requested.gatt.disconnect();
    throw new Error('No writable characteristic on this printer');
  }

  // Wire disconnect cleanup
  requested.addEventListener?.('gattserverdisconnected', () => {
    device = null;
    writeChar = null;
  });

  device = requested;
  writeChar = chosen;
  return requested.name ?? 'Unknown printer';
}

export function disconnectPrinter() {
  try {
    device?.gatt?.disconnect();
  } catch {
    /* ignore */
  }
  device = null;
  writeChar = null;
}

/** Send raw bytes to the printer, chunked for older BLE stacks. */
async function writeBytes(bytes: Uint8Array): Promise<void> {
  if (!writeChar) throw new Error('Printer not connected');
  const CHUNK = 100;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    const slice = bytes.slice(i, i + CHUNK);
    const buf = slice.buffer.slice(slice.byteOffset, slice.byteOffset + slice.byteLength) as ArrayBuffer;
    if (writeChar.writeValueWithoutResponse) await writeChar.writeValueWithoutResponse(buf);
    else if (writeChar.writeValue) await writeChar.writeValue(buf);
  }
}

// ─── ESC/POS receipt builder ────────────────────────────────────────────────

const ESC = 0x1b;
const GS = 0x1d;

function append(out: number[], s: string) {
  const bytes = new TextEncoder().encode(s);
  for (const b of bytes) out.push(b);
}

function divider(width: number, ch = '-'): string {
  return ch.repeat(width) + '\n';
}

function row(label: string, value: string, width: number): string {
  // Clip an over-long label so label + at least one space + value always fits
  // the paper's column count — otherwise the price wraps and every following
  // row misaligns. Long drink/modifier names (now with an x{qty} suffix) hit
  // this on a 58mm (32-col) roll.
  const maxLabel = Math.max(1, width - value.length - 1);
  const clipped = label.length > maxLabel ? label.slice(0, maxLabel) : label;
  const pad = Math.max(1, width - clipped.length - value.length);
  return clipped + ' '.repeat(pad) + value + '\n';
}

function buildEscPos(order: OrderDTO, paperWidth: 58 | 80): Uint8Array {
  const cols = paperWidth === 58 ? 32 : 48;
  const out: number[] = [];

  // Init
  out.push(ESC, 0x40);
  // Center
  out.push(ESC, 0x61, 0x01);
  // Double-size title
  out.push(ESC, 0x21, 0x30);
  append(out, 'HOOPLA POS\n');
  // Reset
  out.push(ESC, 0x21, 0x00);
  append(out, '\n');

  // Order number
  out.push(ESC, 0x21, 0x10); // wide
  append(out, `Order #${order.id}\n`);
  out.push(ESC, 0x21, 0x00);

  // Left align
  out.push(ESC, 0x61, 0x00);
  append(out, `${new Date(order.createdAt).toLocaleString()}\n`);
  if (order.customerPhone) append(out, `Customer: ${order.customerPhone}\n`);
  append(out, divider(cols));

  // Items — each drink (with quantity) followed by its modifiers, indented.
  // Plain ASCII "x2" (not "×") since thermal printers have a limited charset.
  for (const { drink, modifiers } of groupOrderItems(order.items ?? [])) {
    const dq = itemQty(drink);
    const name = dq > 1 ? `${drink.name} x${dq}` : drink.name;
    append(out, row(name, `${Math.round((drink.price * dq) / 100)} UZS`, cols));
    for (const mod of modifiers) {
      const mq = itemQty(mod);
      const mName = '  ' + (mq > 1 ? `${mod.name} x${mq}` : mod.name);
      const mPrice = mod.price > 0 ? `+${Math.round((mod.price * mq) / 100)}` : '';
      append(out, row(mName, mPrice, cols));
    }
  }
  append(out, divider(cols));

  // Customer's note, if any.
  if (order.comment) {
    append(out, `Comment: ${order.comment}\n`);
    append(out, divider(cols));
  }

  // Total
  out.push(ESC, 0x21, 0x10); // wide
  append(out, row('TOTAL', `${Math.round(order.productPrice / 100)} UZS`, cols));
  out.push(ESC, 0x21, 0x00);

  append(out, '\n');
  out.push(ESC, 0x61, 0x01);
  append(out, 'Ordered via Hoopla\n');
  append(out, 'Thank you!\n\n\n');

  // Cut
  out.push(GS, 0x56, 0x00);

  return new Uint8Array(out);
}

export async function printViaBluetooth(order: OrderDTO, paperWidth: 58 | 80): Promise<void> {
  if (!isPrinterConnected()) throw new Error('Printer not connected');
  const bytes = buildEscPos(order, paperWidth);
  await writeBytes(bytes);
}
